import React from 'react';
import { Row, Col, Card, CardBody, Spinner, Progress } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { FunnelMetricsDto } from '@/services/analytics/analytics-types';

interface ConversionFunnelSectionProps {
  data?: FunnelMetricsDto;
  isLoading: boolean;
}

const ConversionFunnelSection: React.FC<ConversionFunnelSectionProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="p-5 text-center">
        <Spinner color="primary" />
        <p className="mt-2">Loading conversion funnel...</p>
      </div>
    );
  }

  if (!data || !data.conversionRates) {
    return <div className="p-5 text-center">No funnel data available</div>;
  }

  // Prepare funnel chart data
  const funnelData = [
    { stage: 'Signups', count: data.signups, percentage: 100 },
    { stage: 'Email Verified', count: data.emailVerified, percentage: (data.emailVerified / data.signups) * 100 },
    { stage: 'KYC Started', count: data.kycStarted, percentage: (data.kycStarted / data.signups) * 100 },
    { stage: 'KYC Completed', count: data.kycCompleted, percentage: (data.kycCompleted / data.signups) * 100 },
    { stage: 'First Deposit', count: data.firstDeposit, percentage: (data.firstDeposit / data.signups) * 100 },
    { stage: 'First Hand', count: data.firstHandPlayed, percentage: (data.firstHandPlayed / data.signups) * 100 },
    { stage: 'Repeat Play', count: data.repeatPlay, percentage: (data.repeatPlay / data.signups) * 100 },
  ];

  const chartOptions = {
    chart: {
      type: 'bar' as const,
      height: 400,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '70%',
        distributed: true,
      },
    },
    colors: ['#556ee6', '#34c38f', '#f1b44c', '#50a5f1', '#f46a6a', '#74788d', '#a6b0cf'],
    dataLabels: {
      enabled: true,
      formatter: function (val: number, opts: any) {
        const index = opts.dataPointIndex;
        return `${funnelData[index].count.toLocaleString()} (${val.toFixed(2)}%)`;
      },
      style: {
        fontSize: '12px',
      },
    },
    xaxis: {
      categories: funnelData.map((item) => item.stage),
      max: 100,
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '13px',
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toFixed(2)}%`,
      },
    },
    legend: {
      show: false,
    },
  };

  const chartSeries = [
    {
      name: 'Conversion',
      data: funnelData.map((item) => item.percentage),
    },
  ];

  return (
    <>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-4">User Conversion Funnel</h5>
              <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={400} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col lg={12}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-4">Conversion Rates Between Steps</h5>
              <div className="conversion-rates">
                {/* Signup to Verified */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-medium">Signup → Email Verified</span>
                    <span className="text-primary fw-bold">{data.conversionRates.signupToVerified.toFixed(2)}%</span>
                  </div>
                  <Progress value={data.conversionRates.signupToVerified} color="primary" className="progress-sm" />
                </div>

                {/* Verified to KYC Started */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-medium">Email Verified → KYC Started</span>
                    <span className="text-success fw-bold">
                      {data.conversionRates.verifiedToKycStarted.toFixed(2)}%
                    </span>
                  </div>
                  <Progress value={data.conversionRates.verifiedToKycStarted} color="success" className="progress-sm" />
                </div>

                {/* KYC Started to Completed */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-medium">KYC Started → KYC Completed</span>
                    <span className="text-warning fw-bold">
                      {data.conversionRates.kycStartedToCompleted.toFixed(2)}%
                    </span>
                  </div>
                  <Progress
                    value={data.conversionRates.kycStartedToCompleted}
                    color="warning"
                    className="progress-sm"
                  />
                </div>

                {/* KYC to Deposit */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-medium">KYC Completed → First Deposit</span>
                    <span className="text-info fw-bold">{data.conversionRates.kycToDeposit.toFixed(2)}%</span>
                  </div>
                  <Progress value={data.conversionRates.kycToDeposit} color="info" className="progress-sm" />
                </div>

                {/* Deposit to Play */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-medium">First Deposit → First Hand Played</span>
                    <span className="text-danger fw-bold">{data.conversionRates.depositToPlay.toFixed(2)}%</span>
                  </div>
                  <Progress value={data.conversionRates.depositToPlay} color="danger" className="progress-sm" />
                </div>

                {/* Play to Repeat */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-medium">First Hand → Repeat Play</span>
                    <span className="text-secondary fw-bold">{data.conversionRates.playToRepeat.toFixed(2)}%</span>
                  </div>
                  <Progress value={data.conversionRates.playToRepeat} color="secondary" className="progress-sm" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Summary Stats */}
      <Row className="mt-4">
        <Col lg={3}>
          <Card className="bg-primary text-white">
            <CardBody>
              <h5 className="mb-3 text-white">Total Signups</h5>
              <h3 className="mb-0 text-white">{data.signups.toLocaleString()}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="bg-success text-white">
            <CardBody>
              <h5 className="mb-3 text-white">Depositing Users</h5>
              <h3 className="mb-0 text-white">{data.firstDeposit.toLocaleString()}</h3>
              <small className="opacity-75">{((data.firstDeposit / data.signups) * 100).toFixed(2)}% of signups</small>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="bg-warning text-white">
            <CardBody>
              <h5 className="mb-3 text-white">Playing Users</h5>
              <h3 className="mb-0 text-white">{data.firstHandPlayed.toLocaleString()}</h3>
              <small className="opacity-75">
                {((data.firstHandPlayed / data.signups) * 100).toFixed(2)}% of signups
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="bg-info text-white">
            <CardBody>
              <h5 className="mb-3 text-white">Retained Users</h5>
              <h3 className="mb-0 text-white">{data.repeatPlay.toLocaleString()}</h3>
              <small className="opacity-75">{((data.repeatPlay / data.signups) * 100).toFixed(2)}% of signups</small>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ConversionFunnelSection;
